//@flow
import { Meteor } from 'meteor/meteor'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import useRouter from 'use-react-router'
import { toast } from 'react-toastify'
import { Accounts } from 'meteor/accounts-base'
import debounce from 'debounce'

import { Card, CardContent, Collapse, Grow, Link } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import EVENTS from '../constants/events'
import { resetFilters } from '../constants/filters'
import trackEvent from '../lib/trackEvent'
import useTracker from '../lib/useTracker'
import { ShowWarningLawyer } from './ShowLawyer'

import TextInput from './TextInput'
import Button from './Button'
import { useGlobalState } from '../store'
import matchUrl from '../lib/matchUrl'
import { checkEmailValidity } from '../lib/checkValidity'
import HeadTitle from './HeadTitle'

import type { GlobalState } from '../types/globalState'

export const REGISTRATION_TYPE = {
  LAWYER: 'lawyer',
}

const VALIDATION_STATUS = {
  WAITING: 'waiting',
  VALIDATED: 'validated',
  ERROR: 'error',
}

type LoginOrSignupProps = {
  defaultTitle: string,
  registrationType?: string,
}

type FooterLabelProps = {
  props?: any,
}

const useLoginSignupStyle = makeStyles((theme) => ({
  container: {},
  nextButton: {
    marginTop: '1vh',
    width: '100%',
    fontWeight: 'bold',
  },
  spacer: {
    height: '3rem',
  },
}))

const useFooterLabelStyle = makeStyles({
  container: ({ theme }) => ({
    color: theme.colors.grey,
    opacity: '55%',
    fontWeight: 'medium',
    textAlign: 'center',
    fontSize: '13px',
    marginTop: '3rem',
  }),
})

const FooterLabel: React.ComponentType<FooterLabelProps> = (props) => {
  const [theme]: [GlobalState] = useGlobalState('theme')
  const classes = useFooterLabelStyle({ theme })

  return (
    <div className={classes.container}>
      En cliquant sur "Continuer avec Email" au dessus, vous reconnaissez avoir pris connaissance,
      compris et accepté <br /> les &nbsp;
      <Link
        style={{ color: theme.colors.grey }}
        href={matchUrl('cgv', theme) || '#'}
        rel="noopener"
        target="_blank"
      >
        CGUV
      </Link>
      &nbsp; et &nbsp;
      <Link
        style={{ color: theme.colors.grey }}
        href={matchUrl('privacy', theme) || '#'}
        rel="noopener"
        target="_blank"
      >
        politique de confidentialité
      </Link>
      &nbsp; de Call a Lawyer
    </div>
  )
}

let phone = null

const LoginOrSignup: React.ComponentType<LoginOrSignupProps> = ({
  registrationType = '',
  forceNextRouteSignup,
  forceNextRouteLogin,
}) => {
  const [login, setLogin] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [isLogin, setIsLogin] = useState<string | null | ''>(null)
  const [localLoading, setLocalLoading] = useState<boolean>(false)
  const [isEmailValid, setIsEmailValid] = useState<string>(VALIDATION_STATUS.WAITING)
  const [showSignUp, setShowSignUp] = useState<boolean>(true)

  const { history } = useRouter()
  const classes = useLoginSignupStyle()
  const [theme]: GlobalState = useGlobalState('theme')

  const call = useCallback(async (email): Promise<void> => {
    if (localLoading) return

    const checkAccount = await Meteor.promise('checkAccountExist', {
      email,
      whiteLabelId: theme.whiteLabelId,
    })

    if (checkAccount?.authType === 'sms' || checkAccount?.authType === 'password') {
      setIsLogin(checkAccount.authType)
      setPassword(checkAccount.code || '')
      setLocalLoading(false)

      phone = checkAccount.phone
    } else if (typeof checkAccount === 'string') {
      return window.location.replace(`https://${checkAccount}/login?email=${email}`)
    } else {
      setIsLogin('')
      setLocalLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isLogin === 'sms')
      toast.info("Un code d'authentification a été envoyé sur votre numéro de téléphone")
  }, [isLogin])

  const nextStep = () => {
    if (isLogin === 'sms') {
      Accounts.callLoginMethod({
        methodArguments: [
          {
            sms: true,
            phone,
            code: password,
            regionCode: '',
          },
        ],
        userCallback: (err) => {
          if (err)
            toast.error(
              "Le format de téléphone n'a pas pu être vérifié. Contactez l'administrateur"
            )
          else history.push(forceNextRouteLogin || '/')
        },
      })
    } else if (isLogin === 'password') {
      Meteor.loginWithPassword(login, password, function (error, res) {
        if (error) toast.error('Echec de login, vérifiez vos identifiants')
        else history.push(forceNextRouteLogin || '/')
      })
    } else {
      trackEvent(EVENTS.SIGNUP.GO_TO_PRIVACY, { login, registrationType })

      // Si il n'y a pas de route forcé, ça veut dire qu'on est dans le process d'inscription hors appel. Dans ce cas on vide la recherche pour être sur de ne pas polluer le process d'inscription (choix des catégorie)
      if (!forceNextRouteSignup) resetFilters()

      history.push(forceNextRouteSignup || '/privacy', { login, registrationType })
    }
  }

  const callDebounced = useCallback(debounce(call, 500, false), [])

  const handleChangeLogin = (value) => {
    setLogin(value)
    if (value === '') setIsEmailValid(VALIDATION_STATUS.WAITING)
    if (checkEmailValidity(value)) {
      setLocalLoading(true)
      callDebounced(value)
    }
  }

  const content = (
    <>
      <TextInput
        placeholder="Entrez votre adresse email..."
        label="EMAIL"
        inputError={isEmailValid === VALIDATION_STATUS.ERROR && !!login}
        inputValid={login !== '' && isEmailValid === VALIDATION_STATUS.VALIDATED}
        onBlur={() =>
          checkEmailValidity(login)
            ? setIsEmailValid(VALIDATION_STATUS.VALIDATED)
            : setIsEmailValid(VALIDATION_STATUS.ERROR)
        }
        errorDescription={'Email non valide'}
        valueInput={login}
        onChangeValue={handleChangeLogin}
        autoComplete="off"
        fullWidth
        infoText="Mot de passe oublié?"
        infoLink={() => history.push('/forgot-password')}
      />
      {(isLogin === 'password' || isLogin === 'sms') && (
        <TextInput
          valueInput={password}
          onChangeValue={setPassword}
          placeholder={
            isLogin === 'password' ? 'Entrez votre mot de passe...' : 'Entrez votre code OTP...'
          }
          autoComplete="off"
          fullWidth
          label={isLogin === 'password' ? 'MOT DE PASSE' : 'Code SMS'}
          type={isLogin === 'password' ? 'password' : 'text'}
          onKeyPress={(event) => event.key === 'Enter' && nextStep()}
        />
      )}

      <Button
        onClick={nextStep}
        color="secondary"
        loading={localLoading}
        className={classes.nextButton}
        disabled={login === '' || (isLogin && !password) || !checkEmailValidity(login)}
      >
        Continuer avec email
      </Button>
    </>
  )

  return (
    <>
      <Card className={classes.container}>
        <CardContent
          style={{ paddingTop: !showSignUp ? 0 : 24, paddingBottom: !showSignUp ? 0 : 24 }}
        >
          <HeadTitle
            title={
              <FormattedMessage
                id={'LoginOrSignUp.Title'}
                defaultMessage={'Nouveau sur {name} ?'}
                values={{ name: theme.title }}
              />
            }
            subtitle={
              showSignUp && (
                <FormattedMessage
                  id={'LoginOrSignUp.SubTitle'}
                  defaultMessage={'Première chose, dites-nous en un peu plus à propos de vous'}
                />
              )
            }
          />
          {!showSignUp && (
            <div style={{ textAlign: 'center' }}>
              <Button
                variant={'text'}
                color={'secondary'}
                onClick={() => setShowSignUp(true)}
                style={{ fontWeight: 900 }}
              >
                <FormattedMessage id={'LoginOrSignup.Signup'} defaultMessage={"S'inscrire"} />
              </Button>
            </div>
          )}

          <Collapse in={showSignUp}>
            <div className={classes.spacer} />

            {content}

            <FooterLabel />
          </Collapse>
        </CardContent>
      </Card>
      <br />
      <Card>
        <CardContent
          style={{ paddingTop: showSignUp ? 0 : 24, paddingBottom: showSignUp ? 0 : 24 }}
        >
          <HeadTitle
            title={
              <FormattedMessage
                id={'LoginOrSignUp.Title2'}
                defaultMessage={"J'ai déjà un compte {name}"}
                values={{ name: theme.title }}
              />
            }
            subtitle={
              !showSignUp && (
                <FormattedMessage
                  id={'LoginOrSignUp.SubTitle2'}
                  defaultMessage={'Reconnectez-vous pour démarrer un appel'}
                  values={{ name: theme.title }}
                />
              )
            }
          />
          {showSignUp && (
            <div style={{ textAlign: 'center' }}>
              <Button
                variant={'text'}
                color={'secondary'}
                onClick={() => setShowSignUp(false)}
                style={{ fontWeight: 900 }}
              >
                <FormattedMessage id={'LoginOrSignup.Login'} defaultMessage={'Se connecter'} />
              </Button>
            </div>
          )}
          <Collapse in={!showSignUp}>
            <div>
              <div className={classes.spacer} />
              {content}
            </div>
          </Collapse>
        </CardContent>
      </Card>
    </>
  )
}

export default LoginOrSignup
